import { useLayoutEffect, useRef, type RefObject } from 'react'

/**
 * **그림 묶음을 판에 맞춰 키우고 줄인다**(eric 2026-09-28 「장비 모드에서.. 지금.. 화면 크기에 맞게... 부품을 크기 조절해줘」).
 *
 * 🔴 **숫자가 없다.** 판(`box`)의 안쪽 폭·높이와 묶음(`content`)의 제 크기(배율 1 기준)를 재서
 *    「둘 중 작은 비」 하나를 `zoom` 으로 건다 — 폭이 모자라면 폭에, 높이가 모자라면 높이에 맞는다.
 *    해상도·서랍 여닫기·창 크기 변화는 전부 `ResizeObserver` 가 다시 잰다.
 * 🔴 **`transform: scale()` 이 아니라 `zoom` 이다** — 커진 만큼 배치 자리도 같이 커져야 가운데 정렬이 산다
 *    (`monitoring.css` 의 장비 그림 크기 절과 같은 이유).
 * 🔴 **꽉 채우지 않는다**(eric 2026-09-28 「화면을 너무 채웠어. 조금만 크기 줄여줘」). 채울 비는 판(`box`)의
 *    CSS 속성 `--fit-fill`(0~1) 한 곳에서 읽는다 — 없으면 1. 값은 `monitoring.css` 의 `.eqsheet` 에 있다.
 *    최소 배율 `--fit-min` 도 같은 자리에서 읽는다 — 없으면 하한이 없다(`fitScale` 참조).
 *    최대 배율 `--fit-max` 도 같다 — 없으면 상한이 없다(eric 2026-10-02 「크기를 너무 채우는데」 — 넓은 칸에서 그림이 제 크기를 넘어 부풀지 않게).
 * ⚠ 제 크기는 「지금 잰 크기 ÷ 지금 배율」로 되짚는다 — 한 번 건 `zoom` 이 다음 잼을 흔들지 않게.
 * 🔴 **판 위 모서리의 요소(`[data-fit-corner]`)를 피한다**(eric 2026-09-29 — 콘솔 장비 보기의 트레이 둘). 그 요소들은
 *    묶음의 제 크기에 안 들어가고, 묶음이 그것을 덮지 않는 가장 큰 배율을 고른다. 판이 좁으면 모서리 요소를 먼저
 *    줄인다(`--fit-corner-max` → `--fit-corner`). 모서리 요소가 없으면 종전 계산 그대로다.
 * ⚠ `ResizeObserver` 가 없는 곳(jsdom)에서는 아무것도 안 한다 — 배율 1 그대로다.
 */
export function useFitZoom<B extends HTMLElement, C extends HTMLElement>(
  options: FitZoomShare = {},
): readonly [box: RefObject<B | null>, content: RefObject<C | null>] {
  const box = useRef<B>(null)
  const content = useRef<C>(null)
  /* 🔴 공유 상한은 렌더마다 바뀌어도 관찰자를 다시 걸지 않는다 — 최신 값만 읽는다 */
  const cap = useRef(options.cap)
  const onFit = useRef(options.onFit)
  const refit = useRef<(() => void) | undefined>(undefined)

  /* 최신 끈을 렌더 «뒤»에 옮겨 둔다(렌더 중에 ref 를 쓰지 않는다) — 아래 맞춤 효과보다 먼저 돈다 */
  useLayoutEffect(() => {
    cap.current = options.cap
    onFit.current = options.onFit
  })

  useLayoutEffect(() => {
    const outer = box.current
    const inner = content.current
    if (outer === null || inner === null || typeof ResizeObserver === 'undefined') return

    const fit = () => {
      const style = getComputedStyle(outer)
      const availW =
        outer.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
      const availH =
        outer.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom)
      const current = Number(inner.style.zoom === '' ? 1 : inner.style.zoom)
      const rect = inner.getBoundingClientRect()
      if (availW <= 0 || availH <= 0 || rect.width === 0 || rect.height === 0) return
      const naturalW = rect.width / current
      const naturalH = rect.height / current
      const raw = parseFloat(style.getPropertyValue('--fit-fill'))
      const fill = Number.isFinite(raw) && raw > 0 && raw <= 1 ? raw : 1
      const rawMin = parseFloat(style.getPropertyValue('--fit-min'))
      const min = Number.isFinite(rawMin) && rawMin > 0 ? rawMin : 0
      const rawMax = parseFloat(style.getPropertyValue('--fit-max'))
      const max = Number.isFinite(rawMax) && rawMax > 0 ? rawMax : Infinity
      const { w: cornerW, h: cornerH } = fitCorners(outer, style, availW, availH)
      const next = fitScale({
        availW,
        availH,
        naturalW,
        naturalH,
        cornerW,
        cornerH,
        fill,
        min,
        max,
      })
      if (next <= 0) return
      /* 이 칸 혼자의 배율을 알린다(공유 상한을 «건 뒤»가 아니라 «걸기 전» 값 — 상한이 제 입력이 되지 않게) */
      onFit.current?.(next)
      const applied = Math.min(next, cap.current ?? Infinity)
      if (Math.abs(applied - current) < 0.01) return
      inner.style.zoom = String(applied)
      /* 글자(이름표)가 배율을 알아야 줄어들 때 제 크기를 지킨다 — `monitoring.css` 의 `--fit-zoom` 짝 */
      inner.style.setProperty('--fit-zoom', String(applied))
    }
    refit.current = fit

    const observer = new ResizeObserver(fit)
    observer.observe(outer)
    observer.observe(inner)
    // 모서리 요소가 커지고 줄면(이름표 · 「칸 배정 정보 없음」 한 줄) 다시 잰다
    for (const corner of outer.querySelectorAll('[data-fit-corner]')) observer.observe(corner)
    fit()
    return () => {
      observer.disconnect()
      refit.current = undefined
    }
  }, [])

  /* 공유 상한이 바뀌면 다시 맞춘다(칸 크기는 그대로라 관찰자가 안 부른다) */
  useLayoutEffect(() => {
    refit.current?.()
  }, [options.cap])

  return [box, content] as const
}

/**
 * **여러 칸이 한 배율로 서게 하는 끈**(eric 2026-10-02 염색기 「대칭이 되게」 — 트레이와 셰이커의 크기 기준을 같게).
 * - `onFit` — 이 칸 혼자 맞췄을 때의 배율을 알린다(공유 상한을 걸기 «전» 값)
 * - `cap` — 이 값보다 크게 서지 않는다. 부르는 쪽이 칸들의 `onFit` 중 가장 작은 값을 넘기면 모든 칸이 같은 배율이 된다
 * 안 넘기면 종전 그대로다(칸마다 따로).
 */
export interface FitZoomShare {
  cap?: number | undefined
  onFit?: ((scale: number) => void) | undefined
}

/**
 * 판 가운데에 설 묶음의 배율 — 순수 계산(시험이 잠근다).
 *
 * 🔴 **위 모서리의 요소를 피한다**(eric 2026-09-29 — 트레이 둘이 왼쪽 위 · 오른쪽 위에 작게). 묶음은 판
 *    한가운데에 서므로 모서리를 안 덮는 길은 둘이다 — ⓐ 위아래로 모서리 높이만큼 비우거나 ⓑ 좌우로 모서리 폭만큼
 *    비우거나. 둘 중 **더 크게 서는 쪽**을 고른다. 모서리가 없으면(0) 종전 계산 그대로다.
 * 🔴 **최소 배율(`min`)** — 채울 비로 줄인 값이 이보다 작으면 이 값까지 올린다(eric 2026-09-29 — 장비를 줄이되
 *    원래 작은 염색기가 우표가 되지 않게). ⚠ 단 **설 자리의 «채울 비와 꽉 참의 가운데»까지만** 올린다 — 하한 때문에
 *    트레이를 덮지도, 트레이에 딱 붙지도 않는다(1280×760 염색기 실측: 가운데 없이 올리면 이름표가 트레이 옆 1px 에 섰다).
 * ⚠ 내림 — 반올림으로 스크롤 막대가 생기지 않게. 설 자리가 없으면 0 이다(부르는 쪽이 건너뛴다).
 */
export function fitScale(input: {
  availW: number
  availH: number
  naturalW: number
  naturalH: number
  cornerW: number
  cornerH: number
  fill: number
  min?: number
  /** 상한 — 넓은 칸에서도 이 배율을 넘지 않는다. 하한(`min`)보다 뒤에 건다(상한이 이긴다) */
  max?: number
}): number {
  const {
    availW,
    availH,
    naturalW,
    naturalH,
    cornerW,
    cornerH,
    fill,
    min = 0,
    max = Infinity,
  } = input
  const fitIn = (w: number, h: number) => Math.max(0, Math.min(w / naturalW, h / naturalH))
  const best = Math.max(fitIn(availW, availH - 2 * cornerH), fitIn(availW - 2 * cornerW, availH))
  const room = (best * (1 + fill)) / 2
  return Math.floor(Math.min(max, Math.max(best * fill, Math.min(room, min))) * 100) / 100
}

/**
 * **모서리 요소가 판의 일정 몫을 넘지 않게 줄인다** — 좁은 판(작은 창 · 서랍)에서 트레이가 제 크기를 지키면
 * 가운데 장비가 트레이보다 작아진다(1280×760 실측: 장비 184px < 트레이 200px). eric 은 「장비가 중심 · 트레이는 작게」다.
 *
 * 🔴 **숫자가 없다.** 몫은 판(`box`)의 CSS 속성 `--fit-corner-max`(0~1) 한 곳에서 읽는다 — 없으면 줄이지 않는다.
 *    줄인 비는 판에 `--fit-corner`(0~1)로 걸고, 모서리 그림의 `zoom` 이 그 값을 곱한다(`monitoring.css`).
 * ⚠ 제 크기(비 1)에서 한 번 재고 비를 정한다 — 한 번 줄인 값이 다음 잼을 흔들지 않게.
 */
function fitCorners(
  outer: HTMLElement,
  style: CSSStyleDeclaration,
  availW: number,
  availH: number,
): { w: number; h: number } {
  const raw = parseFloat(style.getPropertyValue('--fit-corner-max'))
  if (!(Number.isFinite(raw) && raw > 0 && raw <= 1)) return cornerExtent(outer, style)
  outer.style.setProperty('--fit-corner', '1')
  const full = cornerExtent(outer, style)
  if (full.w === 0 || full.h === 0) return full
  const ratio = Math.min(1, (availW * raw) / full.w, (availH * raw) / full.h)
  const shrink = Math.floor(ratio * 100) / 100
  outer.style.setProperty('--fit-corner', String(shrink))
  return shrink === 1 ? full : cornerExtent(outer, style)
}

/**
 * 판 안쪽(안여백 안) 기준으로 **위 모서리 요소(`[data-fit-corner]`)가 차지한 폭·높이**의 최댓값.
 * 폭은 제 쪽 가장자리에서 잰다(왼쪽 모서리는 왼쪽 끝에서, 오른쪽 모서리는 오른쪽 끝에서).
 * ⚠ 모서리 요소는 판 위에 얹힌 절대 배치라 묶음의 제 크기에 안 들어간다 — 그래서 따로 잰다.
 */
function cornerExtent(outer: HTMLElement, style: CSSStyleDeclaration): { w: number; h: number } {
  const box = outer.getBoundingClientRect()
  const left = box.left + outer.clientLeft + parseFloat(style.paddingLeft)
  const right = box.left + outer.clientLeft + outer.clientWidth - parseFloat(style.paddingRight)
  const top = box.top + outer.clientTop + parseFloat(style.paddingTop)
  let w = 0
  let h = 0
  for (const corner of outer.querySelectorAll<HTMLElement>('[data-fit-corner]')) {
    const rect = corner.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) continue
    const side = corner.dataset['fitCorner'] === 'end' ? right - rect.left : rect.right - left
    w = Math.max(w, side)
    h = Math.max(h, rect.bottom - top)
  }
  return { w, h }
}
